import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import AdminNav, { CREATOR_SIDEBAR_WIDTH, CREATOR_BREAKPOINT } from '../components/AdminNav';
import { theme, font, pillButton, outlineButton, inputStyle, labelStyle } from '../components/adminTheme';
import { useLang } from '../useLang';
import { uploadToB2 } from '../utils/uploadToB2';

function Settings() {
    const navigate = useNavigate();
    const { t } = useLang();
    const [username, setUsername] = useState('');
    const [role, setRole] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [avatarFile, setAvatarFile] = useState(null);
    const [bio, setBio] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [bannerUrl, setBannerUrl] = useState('');
    const [bannerFile, setBannerFile] = useState(null);
    const [applyModal, setApplyModal] = useState(false);
    const [applyRole, setApplyRole] = useState('CREATOR');
    const [applyMessage, setApplyMessage] = useState('');
    const [applySubmitting, setApplySubmitting] = useState(false);
    const [, setApplyDone] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [userFullName, setUserFullName] = useState('');
    const [existingApplication, setExistingApplication] = useState(null);
    const [orgLogoUrl, setOrgLogoUrl] = useState('');
    const [orgLogoFile, setOrgLogoFile] = useState(null);

    useEffect(() => {
        const fetchUser = async () => {
            const { data } = await supabase.auth.getSession();
            if (!data.session) { navigate('/admin'); return; }
            const { data: userData } = await supabase
                .from('users')
                .select('username, role, first_name, last_name, avatar_url, bio, website_url, banner_url, org_logo_url')
                .eq('email', data.session.user.email)
                .single();
            if (userData) {
                setUsername(userData.username);
                setRole(userData.role);
                setUserEmail(data.session.user.email);
                setUserFullName(`${userData.first_name || ''} ${userData.last_name || ''}`.trim());

                const { data: appData } = await supabase
                    .from('applications')
                    .select('*')
                    .eq('email', data.session.user.email)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle();
                if (appData) setExistingApplication(appData);
                setFirstName(userData.first_name || '');
                setLastName(userData.last_name || '');
                setAvatarUrl(userData.avatar_url || '');
                setBio(userData.bio || '');
                setWebsiteUrl(userData.website_url || '');
                setBannerUrl(userData.banner_url || '');
                setOrgLogoUrl(userData.org_logo_url || '');
            }
        };
        fetchUser();
    }, [navigate]);

    const handleApply = async () => {
        setApplySubmitting(true);
        const { error } = await supabase.from('applications').insert({
            full_name: userFullName,
            email: userEmail,
            username,
            role_requested: applyRole,
            message: applyMessage,
            status: 'pending',
        });
        setApplySubmitting(false);
        if (!error) {
            setApplyDone(true);
            setExistingApplication({ status: 'pending', role_requested: applyRole });
            setApplyModal(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        const { data } = await supabase.auth.getSession();
        const email = data.session?.user?.email;

        try {
            let newOrgLogoUrl = orgLogoUrl;
            if (orgLogoFile) {
                const ext = orgLogoFile.name.split('.').pop();
                const timestamp = Date.now();
                const path = `covers/org-logos/${username}_${timestamp}.${ext}`;
                newOrgLogoUrl = await uploadToB2(path, orgLogoFile);
            }

            let newAvatarUrl = avatarUrl;
            if (avatarFile) {
                const ext = avatarFile.name.split('.').pop();
                const timestamp = Date.now();
                const path = `covers/avatars/${username}_${timestamp}.${ext}`;
                newAvatarUrl = await uploadToB2(path, avatarFile);
            }
            let newBannerUrl = bannerUrl;
            if (bannerFile) {
                const ext = bannerFile.name.split('.').pop();
                const timestamp = Date.now();
                const path = `covers/banners/${username}_${timestamp}.${ext}`;
                newBannerUrl = await uploadToB2(path, bannerFile);
            }
            await supabase.from('users').update({
                first_name: firstName,
                last_name: lastName,
                avatar_url: newAvatarUrl,
                bio,
                website_url: websiteUrl,
                banner_url: bannerFile ? newBannerUrl : bannerUrl,
                org_logo_url: newOrgLogoUrl,
            }).eq('email', email);

            setAvatarUrl(newAvatarUrl);
            setAvatarFile(null);
            setBannerUrl(newBannerUrl);
            setBannerFile(null);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (err) {
            console.error('Erreur sauvegarde profil:', err);
        }
        setSaving(false);
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/admin');
    };

    const statusColors = {
        pending: { bg: '#fdf7ea', border: '#f0dfa8', text: '#92400e' },
        approved: { bg: '#f0f6f2', border: '#c9dfd0', text: '#2c6e6a' },
        rejected: { bg: '#fdf2f1', border: '#f3d9d6', text: theme.danger },
    };
    const statusStyle = existingApplication ? (statusColors[existingApplication.status] || statusColors.pending) : null;

    return (
        <div style={{ minHeight: '100vh', background: theme.bg }}>
            <style>{`
                .csettings-content {
                    margin-left: ${CREATOR_SIDEBAR_WIDTH}px;
                    max-width: 860px;
                    padding: 48px 40px 80px;
                    box-sizing: border-box;
                }
                @media (max-width: ${CREATOR_BREAKPOINT}px) {
                    .csettings-content { margin-left: 0; padding: 88px 24px 60px; }
                }
                .csettings-banner {
                    position: relative;
                    width: 100%;
                    height: 200px;
                    background: #f2f1ec;
                    overflow: hidden;
                    cursor: pointer;
                    border: 1px solid ${theme.border};
                    box-sizing: border-box;
                }
                .csettings-banner img { width: 100%; height: 100%; object-fit: cover; display: block; }
                .csettings-banner-empty {
                    width: 100%; height: 100%;
                    display: flex; align-items: center; justify-content: center;
                    font-family: ${font}; font-size: 13px; font-weight: 500; color: ${theme.textMuted};
                }
                .csettings-banner-btn {
                    position: absolute; top: 14px; right: 14px;
                    background: ${theme.card}; border: 1px solid ${theme.border};
                    padding: 7px 14px; font-family: ${font}; font-size: 11.5px; font-weight: 700;
                    color: ${theme.text}; cursor: pointer; box-shadow: 0 2px 8px rgba(14,16,19,0.08);
                }
                .csettings-profile-row {
                    display: flex; align-items: center; gap: 16px;
                    padding: 20px 0; border-bottom: 1px solid ${theme.border}; margin-bottom: 28px;
                    flex-wrap: wrap;
                }
                .csettings-avatar {
                    width: 68px; height: 68px; border-radius: 50%; overflow: hidden; flex-shrink: 0;
                    background: ${theme.bg}; border: 1px solid ${theme.border};
                    box-shadow: 0 3px 10px rgba(14,16,19,0.10);
                    display: flex; align-items: center; justify-content: center; cursor: pointer;
                    font-family: ${font}; font-size: 18px; font-weight: 700; color: ${theme.textMuted};
                }
                .csettings-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
                .csettings-grid {
                    display: grid; grid-template-columns: 1fr 260px; gap: 40px; align-items: start;
                }
                @media (max-width: 700px) {
                    .csettings-grid { grid-template-columns: 1fr; }
                }
                .csettings-side-card {
                    border: 1px solid ${theme.border};
                    background: ${theme.card};
                    box-shadow: 0 1px 3px rgba(14,16,19,0.04);
                    padding: 18px 16px;
                }
                .csettings-dropzone {
                    width: 100%; height: 110px; overflow: hidden; cursor: pointer;
                    border: 1.5px dashed ${theme.border};
                    display: flex; align-items: center; justify-content: center;
                    transition: border-color .2s ease;
                }
                .csettings-dropzone:hover { border-color: ${theme.borderHover}; }
            `}</style>

            <AdminNav username={username} role={role} avatarUrl={avatarUrl} onLogout={handleLogout} activePath="/admin/settings" />

            <div className="csettings-content">
                <p className="wlink" style={{ ...labelStyle, cursor: 'pointer', display: 'inline-block' }} onClick={() => navigate('/admin/dashboard')}>{t('back')}</p>
                <h1 style={{ fontFamily: font, fontWeight: 700, fontSize: 'clamp(20px, 2.4vw, 26px)', letterSpacing: '-0.01em', color: theme.text, margin: '0 0 6px' }}>
                    {t('profileSettings')}
                </h1>
                <p style={{ fontFamily: font, fontSize: '13px', fontWeight: 500, color: theme.textMuted, margin: '0 0 32px' }}>
                    {t('settingsSubtitle')}
                </p>

                <div className="csettings-banner" onClick={() => document.getElementById('bannerInput').click()}>
                    {(bannerFile || bannerUrl) ? (
                        <img src={bannerFile ? URL.createObjectURL(bannerFile) : bannerUrl} alt="banner" />
                    ) : (
                        <div className="csettings-banner-empty">{t('clickAddBanner')}</div>
                    )}
                    <button
                        className="csettings-banner-btn"
                        onClick={(e) => { e.stopPropagation(); document.getElementById('bannerInput').click(); }}
                    >
                        {t('updateBanner')}
                    </button>
                </div>
                <input id="bannerInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setBannerFile(e.target.files[0])} />

                <div className="csettings-profile-row">
                    <div className="csettings-avatar" onClick={() => document.getElementById('avatarInput').click()}>
                        {(avatarFile || avatarUrl) ? (
                            <img src={avatarFile ? URL.createObjectURL(avatarFile) : avatarUrl} alt="avatar" />
                        ) : (
                            username?.slice(0, 2).toUpperCase()
                        )}
                    </div>
                    <input id="avatarInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setAvatarFile(e.target.files[0])} />

                    <div style={{ flex: 1, minWidth: '160px' }}>
                        <p style={{ fontFamily: font, fontSize: '14px', fontWeight: 700, color: theme.text, margin: '0 0 2px' }}>{username}</p>
                        <p style={{ fontFamily: font, fontSize: '12px', fontWeight: 500, color: theme.textMuted, margin: 0 }}>@{username} · {role || 'MEMBER'}</p>
                    </div>

                    <button className="wlink" style={outlineButton} onClick={() => document.getElementById('avatarInput').click()}>
                        {t('updatePhoto')}
                    </button>
                </div>

                <div className="csettings-grid">
                    <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                            <div>
                                <p style={labelStyle}>{t('firstName')}</p>
                                <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} style={inputStyle} placeholder={t('firstName')} />
                            </div>
                            <div>
                                <p style={labelStyle}>&nbsp;</p>
                                <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} style={inputStyle} placeholder={t('lastName')} />
                            </div>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <p style={labelStyle}>{t('bio')}</p>
                            <textarea
                                value={bio}
                                onChange={e => setBio(e.target.value)}
                                rows={3}
                                placeholder={t('bioPlaceholder')}
                                style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.6' }}
                            />
                            <p style={{ fontFamily: font, fontSize: '10.5px', fontWeight: 500, color: theme.textMuted, margin: '6px 0 0' }}>{t('bioHint')}</p>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <p style={labelStyle}>{t('clickableLink')}</p>
                            <input
                                type="text"
                                value={websiteUrl}
                                onChange={e => setWebsiteUrl(e.target.value)}
                                placeholder="instagram.com/tonhandle"
                                style={inputStyle}
                            />
                            <p style={{ fontFamily: font, fontSize: '10.5px', fontWeight: 500, color: theme.textMuted, margin: '6px 0 0' }}>{t('linkHint')}</p>
                        </div>

                        {role === 'ORGANISATION' && (
                            <div style={{ marginBottom: '16px' }}>
                                <p style={labelStyle}>{t('orgLogo')}</p>
                                <div className="csettings-dropzone" onClick={() => document.getElementById('orgLogoInput').click()}>
                                    {(orgLogoFile || orgLogoUrl) ? (
                                        <img src={orgLogoFile ? URL.createObjectURL(orgLogoFile) : orgLogoUrl} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', padding: '12px' }} />
                                    ) : (
                                        <p style={{ fontFamily: font, fontSize: '12px', fontWeight: 500, color: theme.textMuted, margin: 0 }}>{t('clickUploadLogo')}</p>
                                    )}
                                </div>
                                <input id="orgLogoInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setOrgLogoFile(e.target.files[0])} />
                            </div>
                        )}

                        <button className="wlink" style={{ ...pillButton, marginTop: '8px' }} onClick={handleSave} disabled={saving}>
                            {saving ? t('saving') : saved ? t('saved') : t('saveChanges')}
                        </button>
                    </div>

                    <div>
                        <div className="csettings-side-card">
                            <p style={labelStyle}>{t('accountType')}</p>
                            <p style={{ fontFamily: font, fontSize: '15px', fontWeight: 700, color: theme.text, margin: '0 0 2px' }}>{role || 'MEMBER'}</p>
                            <p style={{ fontFamily: font, fontSize: '11px', fontWeight: 500, color: theme.textMuted, margin: '0 0 14px' }}>{t('currentRole')}</p>

                            {!existingApplication && role !== 'ADMIN' && (
                                <button className="wlink" style={{ ...outlineButton, width: '100%', textAlign: 'center' }} onClick={() => setApplyModal(true)}>
                                    {t('requestRole')}
                                </button>
                            )}

                            {existingApplication && (
                                <div style={{
                                    padding: '10px 12px',
                                    background: statusStyle.bg,
                                    border: `1px solid ${statusStyle.border}`,
                                }}>
                                    <p style={{ fontFamily: font, fontSize: '11.5px', fontWeight: 500, color: statusStyle.text, margin: 0 }}>
                                        {existingApplication.status === 'pending' ? t('pending') : existingApplication.status === 'approved' ? t('approved') : t('rejectedStatus')} — {existingApplication.role_requested}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {applyModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: theme.card, width: 'min(480px, calc(100vw - 48px))', overflow: 'hidden', boxSizing: 'border-box' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: `1px solid ${theme.border}` }}>
                            <p style={{ fontFamily: font, fontSize: '13.5px', fontWeight: 700, margin: 0, color: theme.text }}>{t('requestRoleModalTitle')}</p>
                            <button onClick={() => setApplyModal(false)} className="wlink" style={{ background: 'none', border: 'none', fontSize: '16px', color: theme.textMuted, cursor: 'pointer' }}>✕</button>
                        </div>
                        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div>
                                <p style={labelStyle}>{t('requestedRole')}</p>
                                <select value={applyRole} onChange={e => setApplyRole(e.target.value)} style={inputStyle}>
                                    <option value="CREATOR">{t('roleCreator')}</option>
                                    <option value="ORGANISATION">{t('roleOrg')}</option>
                                </select>
                            </div>
                            <div>
                                <p style={labelStyle}>{t('optionalMessage')}</p>
                                <textarea value={applyMessage} onChange={e => setApplyMessage(e.target.value)} rows={4} placeholder={t('whyRole')} style={{ ...inputStyle, resize: 'vertical' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px' }}>
                                <button className="wlink" style={outlineButton} onClick={() => setApplyModal(false)}>{t('cancel')}</button>
                                <button className="wlink" style={pillButton} onClick={handleApply} disabled={applySubmitting}>
                                    {applySubmitting ? t('submitting') : t('submit')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Settings;